import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import {
  FiUploadCloud, FiEye, FiTrash2, FiFileText, FiCheck, FiStar,
  FiDownload, FiCopy, FiLayers, FiShield, FiExternalLink, FiZap,
  FiCheckCircle, FiRefreshCw, FiImage, FiX
} from 'react-icons/fi';

const TEMPLATE_PREVIEWS = {
  internship: [
    {
      id: 'offer_v2',
      name: 'Official Internship Offer Letter',
      type: 'Letterhead A4',
      badge: 'OFFICIAL DEFAULT',
      category: 'Offer Letter',
      description: 'Formal corporate offer letter with Innovation Hacks letterhead, role specifications, stipend, and authorized signature seal.',
      gradient: 'from-blue-900 via-indigo-900 to-slate-950',
      accentColor: '#38bdf8',
      previewBg: 'bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900',
      fields: ['Candidate Name', 'Intern ID', 'Program Domain', 'Start & End Date', 'Supervisor Seal', 'QR Verification']
    },
    {
      id: 'completion_v1',
      name: 'Executive Internship Completion Certificate',
      type: 'Landscape 297×210mm',
      badge: 'ENTERPRISE GOLD',
      category: 'Certificate',
      description: 'High-prestige completion credential with gold geometric borders, embossed digital seal, and lifetime QR verification.',
      gradient: 'from-emerald-950 via-slate-900 to-teal-950',
      accentColor: '#34d399',
      previewBg: 'bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900',
      fields: ['Intern Name', 'Credential ID', 'Domain Mastery', 'Performance Grade', 'Issued Date', 'Authorized Stamp']
    },
    {
      id: 'excellence_v1',
      name: 'Distinguished Excellence Award',
      type: 'Landscape 297×210mm',
      badge: 'HIGH HONORS',
      category: 'Award',
      description: 'Reserved for top percentile interns demonstrating outstanding innovation, technical mastery, and project execution.',
      gradient: 'from-amber-950 via-slate-900 to-yellow-950',
      accentColor: '#fbbf24',
      previewBg: 'bg-gradient-to-br from-slate-950 via-amber-950 to-slate-900',
      fields: ['Honoree Name', 'Award ID', 'Project Title', 'Evaluation Score', 'Director Signature', 'Blockchain Hash']
    },
  ],
  certificate: [
    {
      id: 'course_v1',
      name: 'Technical Mastery Credential',
      type: 'Landscape 297×210mm',
      badge: 'CORE STANDARD',
      category: 'Certificate',
      description: 'Verified professional completion credential with cryptographic validation and institutional accreditation.',
      gradient: 'from-purple-950 via-slate-900 to-indigo-950',
      accentColor: '#c084fc',
      previewBg: 'bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900',
      fields: ['Recipient Name', 'Certificate ID', 'Specialization', 'Completion Date', 'Accredited Stamp']
    },
    {
      id: 'merit_v1',
      name: 'Merit Honors Credential',
      type: 'Landscape 297×210mm',
      badge: 'TOP 5% MERIT',
      category: 'Certificate',
      description: 'Distinguished merit recognition for exemplary performance and capstone project success.',
      gradient: 'from-rose-950 via-slate-900 to-pink-950',
      accentColor: '#f43f5e',
      previewBg: 'bg-gradient-to-br from-slate-950 via-rose-950 to-slate-900',
      fields: ['Candidate Name', 'Merit ID', 'Honors Domain', 'Evaluator Seal', 'Verification Link']
    },
    {
      id: 'participation_v1',
      name: 'Hackathon & Workshop Participation',
      type: 'Landscape 297×210mm',
      badge: 'OFFICIAL RECOGNITION',
      category: 'Participation',
      description: 'Issued to delegates, participants, and collaborators in Innovation Hacks technical initiatives.',
      gradient: 'from-cyan-950 via-slate-900 to-blue-950',
      accentColor: '#22d3ee',
      previewBg: 'bg-gradient-to-br from-slate-950 via-cyan-950 to-slate-900',
      fields: ['Delegate Name', 'Event Code', 'Participation Role', 'Issue Date', 'Official Signatory']
    },
  ],
};

const DYNAMIC_TAGS = [
  { tag: '{{name}}', label: 'Full Candidate Name', sample: 'Arun Kumar', desc: 'Auto-capitalized full name' },
  { tag: '{{intern_id}}', label: 'Credential / Intern ID', sample: 'INT-2026-0001', desc: 'Unique alphanumeric identifier' },
  { tag: '{{domain}}', label: 'Internship Domain', sample: 'AI & Machine Learning', desc: 'Assigned domain specialization' },
  { tag: '{{start_date}}', label: 'Program Start Date', sample: '01 August 2026', desc: 'Formatted start date' },
  { tag: '{{end_date}}', label: 'Program End Date', sample: '31 August 2026', desc: 'Formatted completion date' },
  { tag: '{{duration}}', label: 'Duration & Mode', sample: '1 Month (Online)', desc: 'Duration specification' },
  { tag: '{{college}}', label: 'Institution / College', sample: 'Sri Venkateswara College', desc: 'Candidate university or college' },
  { tag: '{{qr_code}}', label: 'Instant Verification QR', sample: 'https://verify.innovationhacks.in/INT-2026-0001', desc: 'Embedded cryptographic QR matrix' },
];

export default function Templates() {
  const {
    mode, settings, updateSettings,
    uploadCustomTemplateForType, resetTemplateForType, customTemplatesMap
  } = usePlatform();

  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('default');
  const fileInputRefs = useRef({});

  const templates = TEMPLATE_PREVIEWS[mode] || [];
  const activeTemplateId = settings?.template || templates[0]?.id;

  const setActiveTemplate = (id) => {
    updateSettings({ template: id });
    const selected = templates.find(t => t.id === id);
    toast.success(`"${selected?.name || id}" is now Active for Document Generation!`);
  };

  const handleFileUploadForType = (typeId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.match(/pdf|image/)) return toast.error('Only image files (PNG/JPG) or PDFs are supported.');
    uploadCustomTemplateForType(typeId, file);
    toast.success(`Custom template uploaded for "${typeId}" and activated!`);
    e.target.value = '';
  };

  const copyTag = (tag) => {
    navigator.clipboard.writeText(tag);
    toast.success(`Copied tag ${tag}`);
  };

  return (
    <div className="space-y-8 pb-16">
      {/* Header Banner */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-md bg-brand-50 border border-brand-200 text-brand-700 text-[11px] font-black uppercase tracking-wider">
              Document Generation Suite
            </span>
            <span className="text-xs text-slate-500 font-bold">•</span>
            <span className="text-xs text-slate-700 font-bold uppercase tracking-wide">Triple Custom Template Studio</span>
          </div>
          <h1 className="page-title capitalize">Innovation Hacks {mode} Templates</h1>
          <p className="page-subtitle">
            Upload custom designs independently for all 3 document types (Offer Letter, Completion Certificate, Excellence Award). Whichever you set as <strong>Active</strong> will be used during certificate generation.
          </p>
        </div>

        {/* Global Active Indicator */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-2xl text-xs font-black shadow-xs">
          <span className="w-2.5 h-2.5 rounded-full bg-brand-600 animate-pulse" />
          <span>Active for Generation: <strong className="text-slate-950 font-black">{templates.find(t => t.id === activeTemplateId)?.name || activeTemplateId}</strong></span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 bg-surface-100 p-1.5 rounded-xl w-fit border border-surface-200">
        {[
          { id: 'default', label: `All 3 Document Types & Custom Designs` },
          { id: 'variables', label: 'Dynamic Variable Studio' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-5 py-2.5 rounded-lg text-xs font-black transition-all ${
              activeTab === t.id
                ? 'bg-white text-slate-950 shadow-sm'
                : 'text-slate-700 hover:text-slate-950'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 3 DOCUMENT TYPES WITH INDEPENDENT CUSTOM UPLOADS ── */}
      {activeTab === 'default' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {templates.map((tpl) => {
            const isActive = activeTemplateId === tpl.id;
            const customUpload = customTemplatesMap?.[tpl.id];

            return (
              <motion.div
                key={tpl.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`glass-card rounded-3xl overflow-hidden flex flex-col justify-between transition-all duration-200 border ${
                  isActive
                    ? 'border-brand-600 ring-4 ring-brand-500/15 shadow-elevated'
                    : 'border-surface-200 hover:border-brand-300 hover:shadow-card'
                }`}
              >
                {/* ── Visual Template Mockup Canvas ── */}
                <div
                  className={`relative h-64 ${tpl.previewBg} p-6 flex flex-col justify-between overflow-hidden group cursor-pointer`}
                  onClick={() => setPreviewTemplate({ ...tpl, customUpload })}
                >
                  {/* If custom design is uploaded, render the custom image! */}
                  {customUpload?.url && customUpload?.type === 'image' ? (
                    <img
                      src={customUpload.url}
                      alt={tpl.name}
                      className="absolute inset-0 w-full h-full object-cover z-0"
                    />
                  ) : null}

                  {/* Dark gradient overlay if custom image exists to ensure text is legible */}
                  {customUpload?.url && (
                    <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-2xs z-1" />
                  )}

                  {/* Subtle Grid Watermark Pattern */}
                  <div className="absolute inset-0 bg-[radial-gradient(#ffffff0a_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none z-2" />

                  {/* Top Bar inside Mockup */}
                  <div className="flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-2">
                      <img src="/logo.png" alt="IH" className="w-7 h-7 object-contain bg-white/10 backdrop-blur-md rounded-md p-1 border border-white/20" />
                      <span className="text-[10px] font-black text-white tracking-widest uppercase drop-shadow-sm">Innovation Hacks</span>
                    </div>

                    {customUpload ? (
                      <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500 text-white shadow-sm flex items-center gap-1">
                        <FiCheck className="w-3 h-3" /> Custom Design
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-white/15 backdrop-blur-md text-white border border-white/20">
                        {tpl.badge}
                      </span>
                    )}
                  </div>

                  {/* Center Content Mockup */}
                  <div className="my-auto text-center relative z-10 py-2">
                    <div className="text-[9px] tracking-[0.35em] uppercase font-bold text-sky-300 drop-shadow-sm mb-1">
                      {tpl.category}
                    </div>
                    <h4 className="text-sm font-black text-white tracking-wide uppercase px-2 leading-tight drop-shadow-md">
                      {tpl.name}
                    </h4>
                    {customUpload && (
                      <div className="mt-1 text-[10px] text-emerald-300 font-bold drop-shadow-sm">
                        File: {customUpload.name}
                      </div>
                    )}
                    <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 rounded-full border border-white/15 backdrop-blur-sm shadow-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-[10px] text-slate-100 font-mono font-bold">INT-2026-XXXX • VERIFIED</span>
                    </div>
                  </div>

                  {/* Bottom Footer inside Mockup */}
                  <div className="flex items-end justify-between border-t border-white/15 pt-3 relative z-10">
                    <div className="text-[9px] font-mono text-slate-200 drop-shadow-sm">
                      <div>Director Signature</div>
                      <div className="text-white/60 text-[8px]">Innovation Hacks Org</div>
                    </div>
                    <div className="w-7 h-7 bg-white/90 rounded-md p-1 flex items-center justify-center shadow-sm">
                      <div className="w-full h-full bg-slate-900 rounded-[2px]" />
                    </div>
                  </div>

                  {/* Hover Overlay Button */}
                  <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-20">
                    <span className="px-4 py-2 bg-white text-slate-950 rounded-xl text-xs font-black shadow-lg flex items-center gap-1.5">
                      <FiEye className="w-4 h-4 text-brand-600" /> Click to Inspect Spec
                    </span>
                  </div>
                </div>

                {/* ── Template Details & Controls ── */}
                <div className="p-6 bg-white flex-1 flex flex-col justify-between space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-black text-brand-700 uppercase tracking-wider">{tpl.type}</span>
                      {isActive && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-brand-50 text-brand-700 border border-brand-200 text-[10px] font-black uppercase shadow-2xs">
                          <FiCheckCircle className="w-3 h-3 text-brand-600" /> Active Generator
                        </span>
                      )}
                    </div>
                    <h3 className="text-base font-black text-slate-950">{tpl.name}</h3>
                    <p className="text-xs text-slate-700 font-semibold mt-1 leading-relaxed">{tpl.description}</p>
                  </div>

                  {/* Custom Upload Section for THIS Document Type */}
                  <div className="p-3.5 bg-surface-50 rounded-2xl border border-surface-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-black text-slate-950">Custom Template Design:</span>
                      {customUpload ? (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                          Custom Loaded
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-600">Default Built-in</span>
                      )}
                    </div>

                    <input
                      type="file"
                      accept=".pdf,image/*"
                      className="hidden"
                      ref={el => (fileInputRefs.current[tpl.id] = el)}
                      onChange={e => handleFileUploadForType(tpl.id, e)}
                    />

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => fileInputRefs.current[tpl.id]?.click()}
                        className="flex-1 h-9 px-3 bg-white hover:bg-brand-50 text-brand-700 hover:text-brand-800 border border-brand-200 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-2xs"
                      >
                        <FiUploadCloud className="w-3.5 h-3.5" />
                        {customUpload ? 'Change Custom Design' : 'Upload Custom Design'}
                      </button>

                      {customUpload && (
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Reset "${tpl.name}" back to default built-in template?`)) {
                              resetTemplateForType(tpl.id);
                              toast.success(`Reset ${tpl.name} to default!`);
                            }
                          }}
                          title="Reset to default design"
                          className="h-9 w-9 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white border border-rose-200 rounded-xl flex items-center justify-center transition-all shadow-2xs flex-shrink-0"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Dynamic Fields Covered */}
                  <div className="space-y-1.5 pt-1">
                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Dynamic Fields Covered:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {tpl.fields.map((f, fi) => (
                        <span key={fi} className="px-2 py-0.5 bg-surface-100 text-slate-800 rounded-md text-[10px] font-bold border border-surface-200">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-2 flex items-center gap-2.5">
                    <button
                      onClick={() => setPreviewTemplate({ ...tpl, customUpload })}
                      className="h-10 px-4 bg-surface-100 hover:bg-surface-200 text-slate-900 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 border border-surface-200"
                    >
                      <FiEye className="w-3.5 h-3.5" /> Preview
                    </button>

                    {!isActive ? (
                      <button
                        onClick={() => setActiveTemplate(tpl.id)}
                        className="flex-1 h-10 px-5 btn-primary text-xs font-black shadow-sm flex items-center justify-center gap-1.5"
                      >
                        <FiStar className="w-3.5 h-3.5" /> Set Active
                      </button>
                    ) : (
                      <div className="flex-1 h-10 px-4 bg-brand-600 text-white rounded-xl text-xs font-black flex items-center justify-center gap-1.5 shadow-sm shadow-brand-600/20">
                        <FiCheck className="w-4 h-4" /> Active for Generation
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── TAB 2: DYNAMIC VARIABLES STUDIO ────────────────── */}
      {activeTab === 'variables' && (
        <div className="space-y-6">
          <div className="glass-card rounded-2xl p-6 bg-gradient-to-r from-white via-surface-50 to-brand-50/30 border border-surface-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-600 text-white flex items-center justify-center font-bold shadow-xs">
                <FiZap className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-950">Dynamic Tag Interpolation Engine</h3>
                <p className="text-xs text-slate-700 font-semibold">
                  Click any tag below to copy it into your document or email template. The engine automatically maps candidate records in real time.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {DYNAMIC_TAGS.map((t, idx) => (
              <div
                key={idx}
                onClick={() => copyTag(t.tag)}
                className="glass-card rounded-2xl p-5 border border-surface-200 hover:border-brand-400 hover:shadow-card cursor-pointer transition-all group bg-white"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-black text-slate-950">{t.label}</span>
                  <button className="text-slate-400 group-hover:text-brand-600 transition-colors">
                    <FiCopy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <code className="block text-xs font-mono font-bold text-brand-700 bg-brand-50 px-2.5 py-1 rounded-lg border border-brand-200 mb-2">
                  {t.tag}
                </code>
                <div className="text-[11px] text-slate-600 font-semibold">
                  Sample: <span className="font-bold text-slate-900">"{t.sample}"</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Full Spec Preview Modal ─────────────────────────── */}
      <AnimatePresence>
        {previewTemplate && (
          <Modal isOpen={!!previewTemplate} onClose={() => setPreviewTemplate(null)} title={`${previewTemplate.name} — Full Layout Spec`} size="lg">
            <div className="space-y-6">
              <div className={`rounded-2xl ${previewTemplate.previewBg} p-8 text-white text-center shadow-elevated border border-white/10 relative overflow-hidden`}>
                {previewTemplate.customUpload?.url && previewTemplate.customUpload?.type === 'image' && (
                  <img
                    src={previewTemplate.customUpload.url}
                    alt={previewTemplate.name}
                    className="absolute inset-0 w-full h-full object-cover z-0"
                  />
                )}
                {previewTemplate.customUpload?.url && (
                  <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-2xs z-1" />
                )}

                <div className="flex items-center justify-between mb-6 relative z-10">
                  <div className="flex items-center gap-2">
                    <img src="/logo.png" alt="Innovation Hacks" className="w-8 h-8 object-contain bg-white/10 rounded-lg p-1" />
                    <span className="text-xs font-black tracking-widest uppercase">Innovation Hacks</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-sky-300">
                    {previewTemplate.customUpload ? 'CUSTOM TEMPLATE OVERLAY' : 'A4 LANDSCAPE (297×210mm)'}
                  </span>
                </div>

                <div className="py-6 max-w-lg mx-auto space-y-3 relative z-10">
                  <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-sky-300">
                    {previewTemplate.category}
                  </div>
                  <h2 className="text-2xl font-black tracking-wide text-white uppercase">{previewTemplate.name}</h2>
                  <p className="text-xs text-slate-200">
                    This is to certify that <span className="text-white font-bold underline decoration-sky-400">Candidate Name</span> has successfully fulfilled all technical benchmarks in <span className="text-white font-bold">Innovation Hacks Program</span>.
                  </p>
                </div>

                <div className="mt-8 pt-4 border-t border-white/15 flex items-center justify-between text-left text-xs relative z-10">
                  <div>
                    <div className="font-bold text-white font-mono">CREDENTIAL ID: INT-2026-0001</div>
                    <div className="text-slate-300 text-[10px]">ISSUED BY INNOVATION HACKS</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-white font-mono">STATUS: LIFETIME VALID</div>
                    <div className="text-slate-300 text-[10px]">SHA-256 ENCRYPTED</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="btn-secondary text-xs py-2 px-5"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setActiveTemplate(previewTemplate.id);
                    setPreviewTemplate(null);
                  }}
                  className="btn-primary text-xs py-2 px-6 shadow-sm"
                >
                  Set as Active for Generation
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}
